import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '';

const C = {
  terracota: '#C4866A',
  rose:      '#D4A5A0',
  gold:      '#C9A86C',
  bg:        'radial-gradient(ellipse at 50% 25%, #2A1810 0%, #1C1210 50%, #0E0A08 100%)',
};

function epdsColor(score) {
  if (score == null) return 'rgba(255,255,255,0.25)';
  if (score <= 9)  return '#7EC8C8';
  if (score <= 12) return '#C9A86C';
  return '#D4756A';
}

function epdsLabel(severity) {
  return { low: 'Low concern', possible: 'Possible', probable: 'Probable depression' }[severity] || '—';
}

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function daysAgo(iso) {
  if (!iso) return '';
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24));
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  return `${diff} days ago`;
}

export default function EPDSHistory() {
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (!user?.user_id) { setLoading(false); return; }
    try {
      const token = localStorage.getItem('auth_token') || localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/assessments/epds/${user.user_id}`, { headers });
      const list = res.data.assessments || [];
      // Sort newest first
      list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setAssessments(list);
    } catch (_) {}
    setLoading(false);
  };

  const latest = assessments[0] || null;

  // Mini sparkline data (last 6)
  const sparkData = assessments.slice(0, 6).reverse();

  return (
    <div style={{
      minHeight: '100dvh',
      background: C.bg,
      padding: '0 0 100px',
      fontFamily: "'Inter', -apple-system, sans-serif",
    }}>

      {/* Header */}
      <div style={{
        padding: '20px 20px 0',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
      }}>
        <button
          onClick={() => navigate('/matrescence')}
          style={{
            background: 'none', border: 'none', padding: 6,
            cursor: 'pointer', color: 'rgba(255,255,255,0.4)',
          }}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M12 5L7 10L12 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <div>
          <p style={{ margin: 0, fontSize: '0.7rem', color: C.terracota, letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 600, opacity: 0.8 }}>
            EPDS · Edinburgh Scale
          </p>
          <p style={{ margin: '1px 0 0', fontSize: '0.75rem', color: 'rgba(255,255,255,0.25)' }}>
            Wellbeing check-in history
          </p>
        </div>
      </div>

      <div style={{ padding: '24px 20px 0', maxWidth: 480, margin: '0 auto', boxSizing: 'border-box' }}>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 60 }}>
            <div style={{
              width: 28, height: 28, borderRadius: '50%',
              border: '2px solid rgba(196,134,106,0.15)',
              borderTop: `2px solid ${C.terracota}`,
              animation: 'spin 1s linear infinite',
            }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        ) : assessments.length === 0 ? (
          /* Empty state */
          <div style={{ textAlign: 'center', paddingTop: 60 }}>
            <div style={{
              width: 64, height: 64, borderRadius: '50%',
              background: 'rgba(196,134,106,0.08)',
              border: '1px solid rgba(196,134,106,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 20px',
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M12 3V13M12 17V18" stroke={C.terracota} strokeWidth="1.5" strokeLinecap="round"/>
                <circle cx="12" cy="12" r="9" stroke={C.terracota} strokeWidth="1.2"/>
              </svg>
            </div>
            <p style={{ margin: '0 0 6px', fontSize: '1rem', color: 'rgba(255,255,255,0.7)', fontWeight: 500 }}>
              No check-ins yet
            </p>
            <p style={{ margin: '0 0 28px', fontSize: '0.82rem', color: 'rgba(255,255,255,0.3)', lineHeight: 1.5 }}>
              Your EPDS results will appear here after your first check-in.
            </p>
            <button
              onClick={() => navigate('/epds')}
              style={{
                padding: '13px 28px',
                background: `linear-gradient(135deg, ${C.terracota} 0%, ${C.rose} 100%)`,
                border: 'none',
                borderRadius: 26,
                color: '#fff',
                fontSize: '0.92rem',
                fontWeight: 600,
                cursor: 'pointer',
                boxShadow: '0 6px 22px rgba(196,134,106,0.28)',
              }}
            >
              Start my first check-in →
            </button>
          </div>
        ) : (
          <>
            {/* Latest score hero */}
            <div style={{
              background: 'rgba(196,134,106,0.07)',
              border: '1px solid rgba(196,134,106,0.22)',
              borderRadius: 20,
              padding: '22px 20px',
              marginBottom: 20,
              display: 'flex',
              alignItems: 'center',
              gap: 20,
            }}>
              {/* Score circle */}
              <div style={{
                width: 80, height: 80, borderRadius: '50%',
                background: 'rgba(196,134,106,0.08)',
                border: `2px solid ${epdsColor(latest?.epds_score)}`,
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <p style={{ margin: 0, fontSize: '1.7rem', fontWeight: 700, color: epdsColor(latest?.epds_score), lineHeight: 1 }}>
                  {latest?.epds_score ?? '—'}
                </p>
                <p style={{ margin: '2px 0 0', fontSize: '0.65rem', color: 'rgba(255,255,255,0.28)' }}>/30</p>
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ margin: '0 0 3px', fontSize: '0.68rem', color: 'rgba(255,255,255,0.28)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                  Latest check-in
                </p>
                <p style={{ margin: '0 0 5px', fontSize: '0.95rem', fontWeight: 600, color: epdsColor(latest?.epds_score) }}>
                  {epdsLabel(latest?.epds_severity)}
                </p>
                <p style={{ margin: 0, fontSize: '0.75rem', color: 'rgba(255,255,255,0.3)' }}>
                  {formatDate(latest?.created_at)}
                </p>
              </div>
            </div>

            {/* Mini sparkline (last 6 scores) */}
            {sparkData.length > 1 && (
              <div style={{
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.07)',
                borderRadius: 16,
                padding: '14px 16px',
                marginBottom: 20,
              }}>
                <p style={{ margin: '0 0 12px', fontSize: '0.68rem', color: 'rgba(255,255,255,0.28)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                  Trend
                </p>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 44 }}>
                  {sparkData.map((a, i) => {
                    const h = Math.max(6, Math.round((a.epds_score / 30) * 44));
                    return (
                      <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                        <div style={{
                          width: '100%',
                          height: h,
                          borderRadius: 4,
                          background: epdsColor(a.epds_score),
                          opacity: i === sparkData.length - 1 ? 1 : 0.45,
                          transition: 'all 0.3s',
                        }} />
                        <p style={{ margin: 0, fontSize: '0.6rem', color: 'rgba(255,255,255,0.2)' }}>
                          {a.epds_score}
                        </p>
                      </div>
                    );
                  })}
                </div>
                {/* Bands reference */}
                <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
                  {[
                    { color: '#7EC8C8', label: '0–9 Low' },
                    { color: '#C9A86C', label: '10–12 Possible' },
                    { color: '#D4756A', label: '13+ Probable' },
                  ].map(b => (
                    <div key={b.label} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <div style={{ width: 8, height: 8, borderRadius: 2, background: b.color, flexShrink: 0 }} />
                      <span style={{ fontSize: '0.62rem', color: 'rgba(255,255,255,0.25)' }}>{b.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* All check-ins list */}
            <p style={{ margin: '0 0 10px 2px', fontSize: '0.68rem', color: 'rgba(255,255,255,0.28)', letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 600 }}>
              All check-ins ({assessments.length})
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
              {assessments.map((a, i) => (
                <button
                  key={i}
                  onClick={() => setExpanded(expanded === i ? null : i)}
                  style={{
                    background: expanded === i ? 'rgba(196,134,106,0.07)' : 'rgba(255,255,255,0.03)',
                    border: `1px solid ${expanded === i ? 'rgba(196,134,106,0.28)' : 'rgba(255,255,255,0.07)'}`,
                    borderRadius: 14,
                    padding: '13px 16px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    outline: 'none',
                    transition: 'all 0.22s',
                    width: '100%',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    {/* Score dot */}
                    <div style={{
                      width: 40, height: 40, borderRadius: '50%',
                      background: 'rgba(255,255,255,0.04)',
                      border: `1.5px solid ${epdsColor(a.epds_score)}`,
                      display: 'flex', flexDirection: 'column',
                      alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <p style={{ margin: 0, fontSize: '0.88rem', fontWeight: 700, color: epdsColor(a.epds_score), lineHeight: 1 }}>
                        {a.epds_score}
                      </p>
                    </div>
                    <div style={{ flex: 1 }}>
                      <p style={{ margin: 0, fontSize: '0.85rem', color: 'rgba(255,255,255,0.75)', fontWeight: 500 }}>
                        {epdsLabel(a.epds_severity)}
                      </p>
                      <p style={{ margin: '2px 0 0', fontSize: '0.72rem', color: 'rgba(255,255,255,0.28)' }}>
                        {daysAgo(a.created_at)} · {formatDate(a.created_at)}
                      </p>
                    </div>
                    {i === 0 && (
                      <span style={{
                        fontSize: '0.62rem', fontWeight: 600,
                        color: C.terracota,
                        background: 'rgba(196,134,106,0.12)',
                        border: '1px solid rgba(196,134,106,0.25)',
                        borderRadius: 20,
                        padding: '2px 8px',
                        flexShrink: 0,
                      }}>
                        Latest
                      </span>
                    )}
                    {a.crisis_flag && (
                      <span style={{
                        fontSize: '0.62rem', fontWeight: 600,
                        color: '#E07568',
                        background: 'rgba(212,100,90,0.1)',
                        border: '1px solid rgba(212,100,90,0.3)',
                        borderRadius: 20,
                        padding: '2px 8px',
                        flexShrink: 0,
                      }}>
                        ⚠ Flag
                      </span>
                    )}
                    <svg
                      width="14" height="14" viewBox="0 0 14 14" fill="none"
                      style={{ flexShrink: 0, transform: expanded === i ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s', color: 'rgba(255,255,255,0.2)' }}
                    >
                      <path d="M5 3L9 7L5 11" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>

                  {/* Expanded: answer breakdown */}
                  {expanded === i && a.epds_answers && (
                    <div style={{ marginTop: 14, borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 12 }}>
                      {QUESTION_LABELS.map((q, qi) => {
                        const ans = a.epds_answers[qi];
                        const itemScore = (qi === 0 || qi === 1) ? (3 - ans) : ans;
                        return (
                          <div key={qi} style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            padding: '5px 0',
                            borderBottom: qi < 9 ? '1px solid rgba(255,255,255,0.04)' : 'none',
                            gap: 10,
                          }}>
                            <p style={{ margin: 0, fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', lineHeight: 1.35, flex: 1 }}>
                              {q}
                            </p>
                            <span style={{
                              fontSize: '0.72rem',
                              fontWeight: 600,
                              color: itemScore >= 2 ? '#D4756A' : itemScore === 1 ? '#C9A86C' : '#7EC8C8',
                              flexShrink: 0,
                            }}>
                              {itemScore}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </button>
              ))}
            </div>

            {/* New check-in CTA */}
            <button
              onClick={() => navigate('/epds')}
              style={{
                width: '100%',
                padding: '14px',
                background: 'rgba(196,134,106,0.09)',
                border: '1.5px solid rgba(196,134,106,0.28)',
                borderRadius: 26,
                color: C.terracota,
                fontSize: '0.9rem',
                fontWeight: 600,
                cursor: 'pointer',
                outline: 'none',
              }}
            >
              New check-in →
            </button>

            <p style={{
              textAlign: 'center', fontSize: '0.68rem',
              color: 'rgba(255,255,255,0.15)', marginTop: 16, lineHeight: 1.5,
            }}>
              EPDS is a screening tool, not a diagnosis. Always discuss results with your healthcare provider.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

const QUESTION_LABELS = [
  'Laugh / see funny side',
  'Look forward with enjoyment',
  'Blamed myself unnecessarily',
  'Anxious or worried for no reason',
  'Scared or panicky for no reason',
  'Things getting on top of me',
  'So unhappy — difficulty sleeping',
  'Felt sad or miserable',
  'So unhappy — been crying',
  'Thoughts of self-harm',
];
